import clsx from 'clsx';
// import _ from 'lodash';
import React, { useCallback, useReducer, useRef } from 'react';

import ClickAwayListener from '@/src//components/common/ClickAwayListener/ClickAwayListener';
import { cn } from '@/src/util/ui';

import Options from '../Options/Options';

const INITIAL_TAG_INPUT = {
  tagValue: '',
  page: 1,
  showOptions: false,
};

const ACTIONS = {
  SET_PAGE: 'SET_PAGE',
  TOGGLE_SHOW_OPTIONS: 'TOGGLE_SHOW_OPTIONS',
  INPUT_CHANGE: 'INPUT_CHANGE',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_PAGE:
      return {
        ...state,
        page: action.page,
      };
    case ACTIONS.TOGGLE_SHOW_OPTIONS:
      return {
        ...state,
        showOptions: action.showOptions,
      };
    case ACTIONS.INPUT_CHANGE:
      return {
        ...state,
        tagValue: action.tagValue,
        page: action.page,
        showOptions: action.showOptions,
      };
    default:
      return state;
  }
};

const getComputedInputWidth = (input) => (input.length + 1) * 7;

const EditTagInput = (props) => {
  const { options, hasMore, tags, defaultValue, onInputChange, onClickTag, onLoadMore, classes } =
    props;
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const observer = useRef();
  const [tagInput, dispatch] = useReducer(reducer, {
    ...INITIAL_TAG_INPUT,
    tagValue: defaultValue,
  });
  const { showOptions, tagValue } = tagInput;
  const inputWidth = getComputedInputWidth(tagValue) + 'px';

  const toggleShowOptions = useCallback((showOptions) => {
    dispatch({
      type: ACTIONS.TOGGLE_SHOW_OPTIONS,
      showOptions,
    });
  }, []);

  const changeInput = useCallback((inputValue) => {
    dispatch({
      type: ACTIONS.INPUT_CHANGE,
      tagValue: inputValue,
      page: 1,
      showOptions: !!inputValue,
    });
  }, []);

  const lastElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, onLoadMore]
  );

  const handleKeyDown = useCallback(
    (e) => {
      const tagValue = e.target.value;

      if (!tagValue && !tags.length) {
        toggleShowOptions(false);
      }

      if (e.key !== 'Enter' && e.key !== 'Tab') {
        return;
      }

      onClickTag(tagValue);
      toggleShowOptions(false);
    },
    [tags, toggleShowOptions]
  );

  const handleInputChange = useCallback(
    (e) => {
      const inputValue = e.currentTarget.value.toLowerCase();

      onInputChange({ inputValue });
      changeInput(inputValue);
    },
    [options, onInputChange]
  );

  const onSelectOption = useCallback(
    (selectOptions) => {
      const { option, checked } = selectOptions;

      if (!checked) {
        onClickTag(option.value);
      }

      inputRef.current.value = '';
      inputRef.current.focus();
    },
    [tags, inputRef, options]
  );

  const onBlur = useCallback(
    (e) => {
      if (containerRef && containerRef.current && containerRef.current.contains(e.target)) {
        return;
      }

      onClickTag(inputRef.current.value);
    },
    [tags]
  );

  return (
    <React.Fragment>
      <div
        className={cn(
          'tw-group',
          'tw-flex tw-items-center',
          'tw-px-[10px] tw-py-[3px] tw-mr-[5px] tw-mb-[5px]',
          'tw-text-cfo-black',
          classes
        )}
        ref={containerRef}
      >
        <ClickAwayListener onClickAway={onBlur} containerClass={clsx('tw-w-full')}>
          <input
            ref={inputRef}
            autoFocus={true}
            onKeyDown={handleKeyDown}
            onChange={handleInputChange}
            onBlur={onBlur}
            defaultValue={defaultValue}
            className={clsx('tw-max-w-[800px]', 'tw-font-brandonTextMedium')}
            style={{ width: inputWidth }}
          />
        </ClickAwayListener>

        <Options
          showOptions={showOptions}
          options={options}
          tags={tags}
          hasMore={hasMore}
          refs={{
            lastElementRef,
            containerRef,
          }}
          onSelectOption={onSelectOption}
        />
      </div>
    </React.Fragment>
  );
};

export default EditTagInput;
